import React, { useEffect, useState } from 'react';
import { Grid, RadioGroup } from 'ht-styleguide';
import omit from 'lodash/omit';
import get from 'lodash/get';

/* Hooks */
import useHomeHubAnswerController from '@scenes/HomeHub/hooks/hook.answerController';

/* Store */
import { useHomeHubStoreActions } from '@scenes/HomeHub/store/client/homehub.store';

/* Components */
import DeviceDropdownSelector from './DeviceDropdownSelector';
import AddDeviceButton from '../../AddDeviceButton';
import RadioSelector from '../Radio/RadioSelector';

/* Constants & Utils */
import { getGridColumnDetails } from '../utils';
import { customOptions } from '@scenes/HomeHub/homehub.constants';

/* Types */
import { TQuestionAnswer, TQuestionDevice, TSelectedAnswer, TRadioSelectionSelector } from '@scenes/HomeHub/homehub.types';

import styles from '../selectors.module.scss';

import { v4 as uuid } from 'uuid';

export const initialDropdownPanelState: TSelectedAnswer[] = [
  {
    id: uuid(),
    data_type: 'smart-hub',
    data_value: null,
    data_qty: 1,
    showInput: false,
    showModelInput: false,
    entity: {
      model_id: undefined,
      make_id: undefined,
      model_name: '',
      make_name: '',
      qty: 1,
      custom: false,
      idk: false,
    },
  },
];

const DeviceDropdownSelectorGroup = () => {
  /* Store */
  /* See action for a readme */
  const { setSelectedAnswersClientTransient } = useHomeHubStoreActions();

  /* Hooks */
  const { answers, devices, selectedAnswers, gridColumn } = useHomeHubAnswerController();

  const [dropdownPanelList, setDropdownPanelList] = useState<TSelectedAnswer[]>(selectedAnswers);
  const [makeInputValue, setMakeInputValue] = useState<TRadioSelectionSelector>();
  const [modelInputValue, setModelInputValue] = useState<TRadioSelectionSelector>();
  const foundRadioValue =
    answers.reduce((answr: any, val: { id: string | number }) => {
      if (val.id === selectedAnswers?.[0]?.id) {
        return val.id;
      }

      return answr;
    }, '') || '';
  const [radio, setRadio] = useState<string | number | undefined>(foundRadioValue);
  const { lgColumn, lgColumnOffset, mdColumn, mdColumnOffset } = getGridColumnDetails(gridColumn);

  useEffect(() => {
    const IDKMakeId = customOptions('make').I_DONT_KNOW.make_id;
    const didNotFindIdk = !devices.some((d: TQuestionDevice) => d.make_id === IDKMakeId);

    if (didNotFindIdk) {
      devices.push(customOptions('make').I_DONT_KNOW);
      devices.push(customOptions('make').OTHER);

      devices.map((od: TQuestionDevice) => {
        const newModels = [...od.models];

        newModels.push(customOptions('model').I_DONT_KNOW);
        newModels.push(customOptions('model').OTHER);

        od.models = newModels;
        return od;
      });
    }
  }, [devices]);

  useEffect(() => {
    const updatedDropdownPanelList = dropdownPanelList.map((panel: TSelectedAnswer) => {
      const updatedPanel = {
        ...panel,
        entity: {
          ...panel.entity,
          ...(makeInputValue && { make_name: get(makeInputValue, `${panel.id}.make_name`, panel.entity?.make_name ?? '') }),
          ...(modelInputValue && { model_name: get(modelInputValue, `${panel.id}.model_name`, panel.entity?.model_name ?? '') }),
        },
      };

      return updatedPanel;
    });

    const timeoutId = setTimeout(() => {
      setDropdownPanelList(updatedDropdownPanelList as TSelectedAnswer[]);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [makeInputValue, modelInputValue, devices, dropdownPanelList]);

  useEffect(() => {
    /* 1. Pre-Setting: Update custom and idk keys. Kinda silly to have to do this */
    const updatedDropdownPanelList = dropdownPanelList.map((panel: TSelectedAnswer) => {
      // const findMatchingDeviceByMake = devices.find((device: TQuestionDevice) => device.make_id === panel.entity?.make_id);
      const isIDK = panel.entity?.make_id === -1;

      return {
        ...panel,
        entity: {
          ...panel.entity,
          /* IDK adaption */
          idk: isIDK,
          /* Custom adaption: not needed? need more testing */
        },
      };
    });

    /* 2.
      Set it: setSelectedAnswersClientTransient
      NOT HAPPY:
          This is a hack as this component has "transient" state per panel. This is not ideal.
          ideally, this would behave like the other questions, but because of makeModelInput/makeMakeInput
          there is a mismatch of local state require and global. You can't have both.
          Hit this snag last minute with the managing of state, so this fast solution. We MUST fix this component.
    */
    /* 3. Update store only for valid (complete) results */
    const validResults = updatedDropdownPanelList.filter((panel: TSelectedAnswer) => {
      /* Invalid: has other model but no input yet */
      if (panel.entity?.make_name?.trim() && panel.entity?.model_id === 'other' && !panel.entity?.model_name?.trim()) {
        return false;
      }
      /* valid: standard selections: has make_id & model_id but not other model */
      if (panel.entity?.make_name?.trim() && (panel.entity?.model_name?.trim() || panel.entity?.model_id)) {
        return true;
      }

      /* valid: idk: make & model */
      if (panel.entity?.make_id === -1 && panel.entity?.model_id === -1) {
        return true;
      }

      return false;
    });

    if (validResults.length > 0) {
      setSelectedAnswersClientTransient(validResults);
    } else if (!radio) {
      /* Panels are empty, but lets only reset to empty if radio is empty */
      setSelectedAnswersClientTransient([]);
    }
  }, [dropdownPanelList, radio, setSelectedAnswersClientTransient]);

  useEffect(() => {
    if (radio) {
      setSelectedAnswersClientTransient({ id: radio });
    }
  }, [radio, setSelectedAnswersClientTransient]);

  /* --------------- METHODS ---------------- */
  const onButtonClick = () => {
    let dropdownList = [...dropdownPanelList];
    // @ts-ignore
    dropdownList = [...dropdownList, { id: uuid(), make: null, model: null, qty: 1, showInput: false, showModelInput: false }];
    setDropdownPanelList(dropdownList);
  };

  const onRadioChange = (selectedValue: string | number | undefined) => {
    setDropdownPanelList(initialDropdownPanelState);
    setRadio(selectedValue);
  };

  const onPanelClose = (value: any, id: number | string) => {
    const filteredValues = dropdownPanelList.filter(panel => panel?.id !== id);

    updateInputStateByAction(id);
    if (filteredValues.length > 0) {
      setDropdownPanelList(filteredValues);
    } else {
      setDropdownPanelList(initialDropdownPanelState);
    }
    setSelectedAnswersClientTransient([]);
  };

  const onDropdownChange = (selected: any, type: string, id: number | string) => {
    setRadio('');
    const mapped = dropdownPanelList.map(panel => {
      const otherSelection = selected.value === 'other';
      if (type === 'make') {
        return panel.id === id
          ? {
              ...panel,
              showInput: otherSelection,
              showModelInput: otherSelection,
              entity: {
                ...panel.entity,
                make_id: selected.value,
                make_name: otherSelection ? '' : selected.label,
                model_id: [-1, 'other'].includes(selected.value) ? selected.value : undefined,
                model_name: '',
                idk: selected.value === -1,
              },
            }
          : { ...panel };
      }
      return panel.id === id
        ? {
            ...panel,
            showModelInput: selected.value === 'other',
            entity: {
              ...panel.entity,
              model_id: selected.value,
              model_name: otherSelection ? '' : selected.label,
            },
          }
        : { ...panel };
    });

    setDropdownPanelList(mapped);
  };

  const onInputChange = (id: number | string) => (type: string, value: string) => {
    if (type === 'make') {
      setMakeInputValue({ ...makeInputValue, [id]: { make_name: value } } as TRadioSelectionSelector);
    } else {
      setModelInputValue({ ...modelInputValue, [id]: { model_name: value } } as TRadioSelectionSelector);
    }
  };

  const updateInputStateByAction = (id: number | string) => {
    setMakeInputValue(omit(makeInputValue, id));
    setModelInputValue(omit(modelInputValue, id));
  };

  const onInputIconClose = (type: string, id: number | string) => {
    if (type === 'make') {
      const mapped = dropdownPanelList.map((panel: TSelectedAnswer) => {
        updateInputStateByAction(id);

        return panel.id === id
          ? {
              ...panel,
              showInput: false,
              showModelInput: false,
              entity: {
                ...panel.entity,
                make_id: undefined,
                model_id: undefined,
                make_name: '',
                model_name: '',
              },
            }
          : { ...panel };
      });
      setDropdownPanelList(mapped);
    } else {
      const mapped = dropdownPanelList.map(panel => {
        setModelInputValue(omit(modelInputValue, id));
        return panel.id === id
          ? {
              ...panel,
              showModelInput: false,
              entity: {
                ...panel.entity,
                model_id: undefined,
                model_name: '',
              },
            }
          : { ...panel };
      });
      setDropdownPanelList(mapped);
    }
  };

  const onQtyChange = (qty: number, id: number | string) => {
    const mapped = dropdownPanelList.map(panel => (panel.id === id ? { ...panel, data_qty: qty } : { ...panel }));
    setDropdownPanelList(mapped);
  };
  const canShowAddButton = dropdownPanelList.some(panel => panel.entity?.model_id !== undefined && panel.entity?.make_id !== undefined);

  return (
    <Grid.Fluid classes={styles.gridContainer}>
      <Grid.Row classes={styles.deviceRow}>
        <Grid.Column sm={4} md={mdColumn} mdOffset={mdColumnOffset} lg={lgColumn} lgOffset={lgColumnOffset} classes={styles.column}>
          <div className={styles.dropdownContainer}>
            {dropdownPanelList.map(panel => {
              const index = dropdownPanelList.findIndex(p => p.id === panel.id);

              return (
                <DeviceDropdownSelector
                  key={panel.id}
                  canCloseDevice={index !== 0}
                  showCloseIcon={dropdownPanelList.length > 1}
                  showDeviceInput={panel.showInput}
                  showModelInput={panel.showModelInput}
                  onInputIconClose={input => onInputIconClose(input, panel.id!)}
                  options={[...devices]}
                  onDropdownChange={(value, type) => onDropdownChange(value, type, panel.id!)}
                  onQtyChange={qty => onQtyChange(qty, panel.id!)}
                  onPanelIconClick={value => onPanelClose(value, panel.id!)}
                  onInputChange={onInputChange(panel.id!)}
                  gridColumn={gridColumn}
                  value={panel.entity}
                  id={panel.id}
                />
              );
            })}
          </div>
        </Grid.Column>
        {canShowAddButton ? (
          <Grid.Column sm={4} md={4} mdOffset={2} lgOffset={4} lg={3} classes={styles.addDeviceButton}>
            <AddDeviceButton onClick={onButtonClick} />
          </Grid.Column>
        ) : (
          <Grid.Column sm={4} md={4} mdOffset={2} lg={3} lgOffset={4} classes={styles.standaloneRadioContainer}>
            <RadioGroup key="answerSelectorGroup" onChange={onRadioChange} value={radio} disableGrouping containerClassName="width100">
              {answers.map((answer: TQuestionAnswer) => (
                <RadioSelector key={answer.id} label={answer.text} value={answer.id} labelClass="width50" classes={styles.standaloneRadio} />
              ))}
            </RadioGroup>
          </Grid.Column>
        )}
      </Grid.Row>
    </Grid.Fluid>
  );
};

export default React.memo(DeviceDropdownSelectorGroup);
